// Calendar search and filter utilities
import { UnifiedCalendarEvent, CalendarFilters } from '@/types/unifiedCalendar';

/**
 * Search events by text query
 */
export function searchEvents(
  events: UnifiedCalendarEvent[],
  query: string
): UnifiedCalendarEvent[] {
  if (!query || query.trim() === '') {
    return events;
  }
  
  const lowerQuery = query.toLowerCase();
  
  return events.filter((event) => {
    // Search in title
    if (event.title.toLowerCase().includes(lowerQuery)) {
      return true;
    }
    
    // Search in description
    if (event.description && event.description.toLowerCase().includes(lowerQuery)) {
      return true;
    }
    
    // Search in location
    if (event.location && event.location.toLowerCase().includes(lowerQuery)) {
      return true;
    }
    
    // Search in attendee names and emails
    if (event.attendees) {
      for (const attendee of event.attendees) {
        if (
          attendee.email.toLowerCase().includes(lowerQuery) ||
          (attendee.name && attendee.name.toLowerCase().includes(lowerQuery))
        ) {
          return true;
        }
      }
    }
    
    return false;
  });
}

/**
 * Filter events by date range
 */
export function filterEventsByDateRange(
  events: UnifiedCalendarEvent[],
  startDate: Date,
  endDate: Date
): UnifiedCalendarEvent[] {
  const startTime = startDate.getTime();
  const endTime = endDate.getTime();
  
  return events.filter((event) => {
    const eventStart = new Date(event.start).getTime();
    const eventEnd = new Date(event.end).getTime();
    
    // Event overlaps with the date range
    return eventStart < endTime && eventEnd > startTime;
  });
}

/**
 * Filter events by calendar
 */
export function filterEventsByCalendar(
  events: UnifiedCalendarEvent[],
  calendarIds: string[]
): UnifiedCalendarEvent[] {
  if (calendarIds.length === 0) {
    return events;
  }
  
  return events.filter((event) => calendarIds.includes(event.calendarId));
}

/**
 * Filter events by provider
 */
export function filterEventsByProvider(
  events: UnifiedCalendarEvent[],
  providers: Array<"google" | "outlook" | "manual">
): UnifiedCalendarEvent[] {
  if (providers.length === 0) {
    return events;
  }
  
  return events.filter((event) => providers.includes(event.provider));
}

/**
 * Filter all-day events
 */
export function filterAllDayEvents(
  events: UnifiedCalendarEvent[],
  includeAllDay: boolean
): UnifiedCalendarEvent[] {
  return events.filter((event) => event.allDay === includeAllDay);
}

/**
 * Filter recurring events
 */
export function filterRecurringEvents(
  events: UnifiedCalendarEvent[],
  includeRecurring: boolean
): UnifiedCalendarEvent[] {
  return events.filter((event) => event.recurring === includeRecurring);
}

/**
 * Comprehensive filter function
 */
export function filterEvents(
  events: UnifiedCalendarEvent[],
  filters: CalendarFilters
): UnifiedCalendarEvent[] {
  let results = events;
  
  // Apply date range filter
  if (filters.dateRange) {
    results = filterEventsByDateRange(results, filters.dateRange.start, filters.dateRange.end);
  }
  
  // Apply calendar filter
  if (filters.calendarIds && filters.calendarIds.length > 0) {
    results = filterEventsByCalendar(results, filters.calendarIds);
  }
  
  // Apply provider filter
  if (filters.providers && filters.providers.length > 0) {
    results = filterEventsByProvider(results, filters.providers);
  }
  
  // Apply all-day filter
  if (filters.includeAllDay !== undefined) {
    results = filterAllDayEvents(results, filters.includeAllDay);
  }
  
  // Apply recurring filter
  if (filters.includeRecurring !== undefined) {
    results = filterRecurringEvents(results, filters.includeRecurring);
  }
  
  // Apply text search
  if (filters.query) {
    results = searchEvents(results, filters.query);
  }
  
  return results;
}

/**
 * Sort events by start time
 */
export function sortEventsByStartTime(
  events: UnifiedCalendarEvent[],
  ascending: boolean = true
): UnifiedCalendarEvent[] {
  return [...events].sort((a, b) => {
    const timeA = new Date(a.start).getTime();
    const timeB = new Date(b.start).getTime();
    return ascending ? timeA - timeB : timeB - timeA;
  });
}

/**
 * Sort events by duration
 */
export function sortEventsByDuration(
  events: UnifiedCalendarEvent[],
  ascending: boolean = true
): UnifiedCalendarEvent[] {
  return [...events].sort((a, b) => {
    const durationA = new Date(a.end).getTime() - new Date(a.start).getTime();
    const durationB = new Date(b.end).getTime() - new Date(b.start).getTime();
    return ascending ? durationA - durationB : durationB - durationA;
  });
}
